import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Card, CardBody, Row, Col, Badge, Nav, NavItem, NavLink, TabContent, TabPane, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { new_plans } from '../Config/new-plans';
import { createPassLink } from '../ReduxToolkit/Reducers/organizationManagement';
import * as yup from 'yup';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from 'lodash.debounce';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import ExistingPlanChoice from './ExistingPlanChoice';

const customPlanSchema = yup.object().shape({
  plan_type: yup
    .string()
    .required('Plan type is required')
    .oneOf(['annual', 'monthly'], 'Plan type must be either annual or monthly'),
  credits: yup
    .number()
    .typeError('Credits must be a number')
    .required('Credits is required')
    .positive('Credits must be a positive number')
    .integer('Credits must be a whole number')
    .min(1, 'Credits must be at least 1'),
});

const SubscribeHyperscoutPlanModal = ({ open, onClose, organization, onSubmit, onToggleSubscription, onCancelSubscription }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('annual');
  const [isSubscriptionPaused, setIsSubscriptionPaused] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [existingPlanAction, setExistingPlanAction] = useState(null);
  const needsChoice = organization?.is_premium && !existingPlanAction;
  const dispatch = useDispatch();
  const [passLinkUrl, setPassLinkUrl] = useState('');
  // Off for renewals: the card is charged as soon as it's saved
  const [passLinkTrial, setPassLinkTrial] = useState(true);

  // Payment link for this org's admin: the choice above is stored on the link and applied when the
  // customer saves the card (Seasonal-Pass.md Phase 2)
  const handleCreatePassLink = async () => {
    if (needsChoice) return;
    const result = await dispatch(
      createPassLink({
        organization_id: organization.id,
        existing_plan_action: existingPlanAction || undefined,
        allow_trial: passLinkTrial,
      })
    );
    if (result?.statusCode === 200) setPassLinkUrl(result.data.url);
  };

  const copyPassLinkUrl = () =>
    navigator.clipboard.writeText(passLinkUrl).then(() => toast.success('Link copied', { autoClose: 2000 }));

  const { control, reset, handleSubmit: handleFormSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(customPlanSchema),
    defaultValues: {
      plan_type: 'annual',
      credits: '',
    }
  });

  // Set the selected plan to the current plan if organization has one
  useEffect(() => {
    if (open && organization?.plan_details?.id !== undefined && new_plans.length > 0) {
      // By type too: a custom plan's id (credits + organization id) can equal a plan's id
      const currentPlan = new_plans.find(
        plan => plan.id === organization?.plan_details?.id && plan.type === organization?.plan_details?.type
      );
      if (currentPlan) {
        setSelectedPlan(currentPlan);
        // Set the active tab based on the current plan type
        if (currentPlan.type === 'annual') {
          setActiveTab('annual');
        } else if (currentPlan.type === 'monthly') {
          setActiveTab('monthly');
        } else if (currentPlan.type === 'seasonal_pass') {
          setActiveTab('seasonal_pass');
        }
      }
    }
  }, [open, organization, new_plans]);

  // Sync subscription paused state
  useEffect(() => {
    if (open && organization) {
      setIsSubscriptionPaused(organization.is_subscription_paused || false);
    }
  }, [open, organization]);

  // Reset form when modal opens (but don't reset selected plan or active tab)
  useEffect(() => {
    if (open) {
      reset({
        plan_type: 'annual',
        credits: '',
      });
    }
  }, [open, reset]);

  const handleSubmit = async () => {
    if (activeTab === 'custom') {
      // Handle custom plan submission through form validation
      return;
    }

    if (!selectedPlan || needsChoice) return;

    setIsSubmitting(true);
    try {
      await onSubmit(organization, selectedPlan.id, null, existingPlanAction);
      setSelectedPlan(null);
      setExistingPlanAction(null);
      onClose();
    } catch (e) {
      console.error('Error subscribing to plan:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomPlanSubmit = async (values) => {
    if (needsChoice) return;
    setIsSubmitting(true);
    try {
      const customPlanData = {
        plan_type: values.plan_type,
        credits: values.credits,
      };

      // Pass null for planId and customPlanData as third parameter
      await onSubmit(organization, null, customPlanData, existingPlanAction);
      reset();
      setExistingPlanAction(null);
      onClose();
    } catch (e) {
      console.error('Error subscribing to custom plan:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPlan(null);
    setExistingPlanAction(null);
    setPassLinkUrl('');
    setPassLinkTrial(true);
    reset();
    onClose();
  };

  // Debounced API call for toggle subscription
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedApiCall = useCallback(
    debounce(async (org, newPausedState) => {
      if (!onToggleSubscription) return;

      try {
        await onToggleSubscription(org, newPausedState);
      } catch (error) {
        console.error('Error toggling subscription:', error);
      }
    }, 500),
    [onToggleSubscription]
  );

  const handleToggleSubscription = () => {
    if (!organization?.is_premium || !onToggleSubscription) return;

    // Update UI immediately
    const newPausedState = !isSubscriptionPaused;
    setIsSubscriptionPaused(newPausedState);

    // Debounce the API call
    debouncedApiCall(organization, newPausedState);
  };

  const handleCancelSubscription = async () => {
    if (!organization?.id || !onCancelSubscription) return;

    setIsCancelling(true);
    try {
      const result = await onCancelSubscription(organization.id);
      if (result?.statusCode === 200) {
        setShowCancelConfirm(false);
        onClose();
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const annualPlans = new_plans.filter(plan => plan.type === 'annual');
  const monthlyPlans = new_plans.filter(plan => plan.type === 'monthly');
  const passPlans = new_plans.filter(plan => plan.type === 'seasonal_pass');

  const getOrganizationTypeLabel = () => {
    const orgType = organization?.type?.toLowerCase() || '';
    if (orgType === 'brand' || orgType === 'brands') return 'Brands';
    if (orgType === 'retailer' || orgType === 'retailers') return 'Retailers';
    if (orgType === 'sales') return 'Sales';
    return organization?.type || 'N/A';
  };

  return (
    <Modal isOpen={open} toggle={handleClose} centered size="xl">
      <ModalHeader toggle={handleClose}>
        <span style={{ fontSize: "18px", fontWeight: "700", marginRight: '20px' }}>
          Subscribe Hyperscout Plan
        </span>
        {organization?.is_premium && (
          <Button color="danger" onClick={() => setShowCancelConfirm(true)}>Cancel Subscription</Button>
        )}
      </ModalHeader>
      <ModalBody>
        {organization && (
          <Alert color="info" className="mb-3">
            <div><strong>Organization:</strong> {organization.name || 'N/A'}</div>
            <div><strong>Website:</strong> {organization.website || 'N/A'}</div>
            <div><strong>Type:</strong> {organization.type || 'N/A'}</div>
            <div><strong>Current Premium Status:</strong> {organization.is_premium ? 'Yes' : 'No'}</div>
          </Alert>
        )}

        <ExistingPlanChoice organization={organization} value={existingPlanAction} onChange={setExistingPlanAction} />

        {/* Subscription Toggle - Only show for premium organizations */}
        {organization?.is_premium && (
          <div className="d-flex align-items-center justify-content-between mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <div>
              <strong style={{ fontSize: '14px' }}>{organization?.is_on_trial ? "Trial Subscription Status" : "Subscription Status"}:</strong>
              <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'bold', color: isSubscriptionPaused ? '#856404' : '#2e7d32' }}>
                {isSubscriptionPaused ? 'Paused' : 'Active'}
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="subscriptionToggle"
                checked={!isSubscriptionPaused}
                onChange={handleToggleSubscription}
                style={{ cursor: 'pointer', width: '48px', height: '24px' }}
              />
              <label className="form-check-label ms-2" htmlFor="subscriptionToggle" style={{ fontSize: '14px', fontWeight: '500' }}>
                {isSubscriptionPaused ? 'Resume Subscription' : 'Pause Subscription'}
              </label>
            </div>
          </div>
        )}

        <h5 className="mb-3" style={{ fontWeight: "600" }}>
          Hyperscout Plans - {getOrganizationTypeLabel()}
        </h5>

        {/* Tabs */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'annual' ? 'active' : ''}
              onClick={() => { setActiveTab('annual') }}
              style={{ cursor: 'pointer' }}
            >
              Annual Plans
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'monthly' ? 'active' : ''}
              onClick={() => { setActiveTab('monthly') }}
              style={{ cursor: 'pointer' }}
            >
              Monthly Plans
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'custom' ? 'active' : ''}
              onClick={() => { setActiveTab('custom') }}
              style={{ cursor: 'pointer' }}
            >
              Custom Plan
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'seasonal_pass' ? 'active' : ''}
              onClick={() => { setActiveTab('seasonal_pass'); setSelectedPlan(passPlans[0]) }}
              style={{ cursor: 'pointer' }}
            >
              Seasonal Pass
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* Annual Plans Tab */}
          <TabPane tabId="annual">
            {annualPlans.length === 0 ? (
              <Alert color="warning">
                No annual plans available for this organization type.
              </Alert>
            ) : (
              <Row>
                {annualPlans.map((plan) => {
                  const isCurrentPlan = organization?.plan_id === plan.id;
                  return (
                    <Col key={plan.id} xs={12} md={6} lg={4} xl={3} className="mb-3">
                      <Card
                        style={{
                          cursor: 'pointer',
                          border: selectedPlan?.id === plan.id ? '2px solid #007bff' : '1px solid #ddd',
                          backgroundColor: selectedPlan?.id === plan.id ? '#f0f8ff' : 'white',
                          height: '100%',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => setSelectedPlan(plan)}
                      >
                        <CardBody className="d-flex flex-column">
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1" style={{ fontWeight: "700", fontSize: '16px' }}>
                                {plan.name}
                              </h6>
                              {isCurrentPlan && (
                                <Badge color="success" style={{ fontSize: '10px' }}>Current</Badge>
                              )}
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: '700', color: '#007bff', marginTop: '8px' }}>
                              {plan.currency}{plan.amount}
                              <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>
                                /{plan.type}
                              </span>
                            </div>
                          </div>

                          <div className="flex-grow-1" style={{ fontSize: '13px' }}>
                            <div className="mb-2">
                              <strong>Credits:</strong> {plan.max_credits === 999999999999999 ? 'Unlimited' : plan.max_credits}
                            </div>
                          </div>

                          {isCurrentPlan && (
                            <Button
                              color="primary"
                              size="sm"
                              className="mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(plan);
                                handleSubmit();
                              }}
                              style={{ fontSize: '12px' }}
                            >
                              Renew Current Plan
                            </Button>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            {!selectedPlan && annualPlans.length > 0 && (
              <div className="text-muted text-center mt-3" style={{ fontSize: '14px' }}>
                Please select a plan to continue
              </div>
            )}
          </TabPane>

          {/* Monthly Plans Tab */}
          <TabPane tabId="monthly">
            {monthlyPlans.length === 0 ? (
              <Alert color="warning">
                No monthly plans available for this organization type.
              </Alert>
            ) : (
              <Row>
                {monthlyPlans?.filter((p) => p?.name !== 'Free').map((plan) => {
                  const isCurrentPlan = organization?.plan_details?.id === plan.id;
                  return (
                    <Col key={plan.id} xs={12} md={6} lg={4} xl={3} className="mb-3">
                      <Card
                        style={{
                          cursor: 'pointer',
                          border: selectedPlan?.id === plan.id ? '2px solid #007bff' : '1px solid #ddd',
                          backgroundColor: selectedPlan?.id === plan.id ? '#f0f8ff' : 'white',
                          height: '100%',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => setSelectedPlan(plan)}
                      >
                        <CardBody className="d-flex flex-column">
                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1" style={{ fontWeight: "700", fontSize: '16px' }}>
                                {plan.name}
                              </h6>
                              {isCurrentPlan && (
                                <Badge color="success" style={{ fontSize: '10px' }}>Current</Badge>
                              )}
                            </div>
                            <div style={{ fontSize: '28px', fontWeight: '700', color: '#007bff', marginTop: '8px' }}>
                              {plan.currency}{plan.amount}
                              <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>
                                /{plan.type}
                              </span>
                            </div>
                          </div>

                          <div className="flex-grow-1" style={{ fontSize: '13px' }}>
                            <div className="mb-2">
                              <strong>Credits:</strong> {plan.max_credits === 999999999999999 ? 'Unlimited' : plan.max_credits}
                            </div>
                          </div>

                          {isCurrentPlan && (
                            <Button
                              color="primary"
                              size="sm"
                              disabled={isSubmitting || (activeTab !== 'custom' && !selectedPlan)}
                              className="mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(plan);
                                handleSubmit();
                              }}
                              style={{ fontSize: '12px' }}
                            >
                              {isSubmitting ? (
                                <Spinner size="sm" color="light" />
                              ) : (
                                "Renew Current Plan"
                              )}
                            </Button>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            {!selectedPlan && monthlyPlans.length > 0 && (
              <div className="text-muted text-center mt-3" style={{ fontSize: '14px' }}>
                Please select a plan to continue
              </div>
            )}
          </TabPane>

          {/* Custom Plan Tab */}
          <TabPane tabId="custom">
            <Row>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label className="d-block mb-2" style={{ fontWeight: '600' }}>Plan Type *</Label>
                  <Controller
                    name="plan_type"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="d-flex gap-4">
                        <div className="form-check">
                          <Input
                            type="radio"
                            id="plan_type_annual"
                            name="plan_type"
                            value="annual"
                            checked={value === 'annual'}
                            onChange={(e) => onChange(e.target.value)}
                            className="form-check-input"
                            invalid={!!errors?.plan_type}
                          />
                          <Label className="form-check-label" htmlFor="plan_type_annual" style={{ cursor: 'pointer' }}>
                            Annual
                          </Label>
                        </div>
                        <div className="form-check">
                          <Input
                            type="radio"
                            id="plan_type_monthly"
                            name="plan_type"
                            value="monthly"
                            checked={value === 'monthly'}
                            onChange={(e) => onChange(e.target.value)}
                            className="form-check-input"
                            invalid={!!errors?.plan_type}
                          />
                          <Label className="form-check-label" htmlFor="plan_type_monthly" style={{ cursor: 'pointer' }}>
                            Monthly
                          </Label>
                        </div>
                      </div>
                    )}
                  />
                  {errors?.plan_type && (
                    <FormFeedback style={{ display: 'block' }}>{errors.plan_type.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Credits *</Label>
                  <Controller
                    name="credits"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Input
                        type="number"
                        value={value}
                        onChange={onChange}
                        invalid={!!errors?.credits}
                        placeholder="Enter credits"
                        min="1"
                        step="1"
                        onKeyDown={(e) => {
                          if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                  {errors?.credits && (
                    <FormFeedback style={{ display: 'block' }}>{errors.credits.message}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </TabPane>

          {/* Seasonal Pass Tab: activates now; the payment-link route is the customer's card + 7-day trial */}
          <TabPane tabId="seasonal_pass">
            <Row>
              {passPlans.map((plan) => (
                <Col key={plan.id} xs={12} md={6} lg={4} className="mb-3">
                  <Card
                    style={{
                      cursor: 'pointer',
                      border: selectedPlan?.id === plan.id ? '2px solid #007bff' : '1px solid #ddd',
                      backgroundColor: selectedPlan?.id === plan.id ? '#f0f8ff' : 'white',
                      height: '100%',
                    }}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <CardBody>
                      <h6 className="mb-1" style={{ fontWeight: "700", fontSize: '16px' }}>
                        {plan.name}
                      </h6>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#007bff', marginTop: '8px' }}>
                        {plan.currency}{plan.amount}
                        <span style={{ fontSize: '14px', fontWeight: '400', color: '#666' }}>/5 months</span>
                      </div>
                      <div className="mt-3" style={{ fontSize: '13px' }}>
                        <div className="mb-2"><strong>Credits:</strong> {plan.max_credits}</div>
                        <div className="text-muted">
                          Starts now, with no card or trial. After 5 months: a 14-day grace with its credits frozen,
                          then it stops. Renew by assigning a new pass with "transfer".
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="mt-2" style={{ fontSize: '13px' }}>
              <strong>Or send a payment link:</strong>{' '}
              {passLinkTrial
                ? `the customer saves a card, gets a 7-day trial and is charged ${passPlans[0]?.currency}${passPlans[0]?.amount} + VAT when it ends.`
                : `the customer saves a card and is charged ${passPlans[0]?.currency}${passPlans[0]?.amount} + VAT at once (renewals).`}{' '}
              Valid 14 days.
            </div>
            {!passLinkUrl && (
              <div className="form-check mt-2">
                <Input
                  type="checkbox"
                  id="org-pass-link-trial"
                  checked={passLinkTrial}
                  onChange={(e) => setPassLinkTrial(e.target.checked)}
                  className="form-check-input"
                />
                <Label className="form-check-label" htmlFor="org-pass-link-trial">
                  Start with a 7-day trial
                </Label>
              </div>
            )}
            {passLinkUrl ? (
              <div className="d-flex gap-2 mt-2">
                <Input value={passLinkUrl} readOnly />
                <Button color="primary" outline onClick={copyPassLinkUrl}>Copy</Button>
              </div>
            ) : (
              <Button color="primary" outline className="mt-2" onClick={handleCreatePassLink} disabled={needsChoice}>
                Create payment link
              </Button>
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <button
          className='btn btn-outline-dark'
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <Button
          color="primary"
          onClick={activeTab === 'custom' ? handleFormSubmit(handleCustomPlanSubmit) : handleSubmit}
          disabled={isSubmitting || (activeTab !== 'custom' && !selectedPlan) || needsChoice}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : activeTab === 'seasonal_pass' ? (
            "Assign now"
          ) : (
            "Subscribe"
          )}
        </Button>
      </ModalFooter>

      {/* Cancel Subscription Confirmation Modal */}
      <CancelSubscriptionModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onSubmit={handleCancelSubscription}
        isLoading={isCancelling}
        organizationName={organization?.name}
      />
    </Modal>
  );
};

export default SubscribeHyperscoutPlanModal;
